import { useEffect, useState } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import AdminAnalytics from "../components/AdminAnalytics";
import CMSEditor from "../components/CMSEditor";
import MediaLibrary from "../components/MediaLibrary";
import PropertiesPanel from "../components/admin/PropertiesPanel";
import BookingsPanel from "../components/admin/BookingsPanel";
import OwnersPanel from "../components/admin/OwnersPanel";
import ConciergePanel from "../components/admin/ConciergePanel";
import SettingsPanel from "../components/admin/SettingsPanel";
import CalendarPanel from "../components/admin/CalendarPanel";
import GuestsPanel from "../components/admin/GuestsPanel";
import PaymentsPanel from "../components/admin/PaymentsPanel";
import AuditLogPanel from "../components/admin/AuditLogPanel";
import FeatureFlagsPanel from "../components/admin/FeatureFlagsPanel";
import BrandSettingsPanel from "../components/admin/BrandSettingsPanel";
import PlatformPropertiesPanel from "../components/admin/PlatformPropertiesPanel";
import KnowledgeBasePanel from "../components/admin/KnowledgeBasePanel";
import ConciergeShadowPanel from "../components/admin/ConciergeShadowPanel";
import ImportKnowledgePanel from "../components/admin/ImportKnowledgePanel";
import ComingSoon from "../components/admin/ComingSoon";

const VALID = new Set([
  "dashboard", "homepage", "media", "seo", "properties", "bookings",
  "calendar", "guests", "payments", "reviews",
  "concierge", "longstay", "owners",
  "brand-settings", "platform-properties", "knowledge-base", "knowledge-import",
  "concierge-shadow", "audit", "flags", "settings",
]);

function readHashTab() {
  if (typeof window === "undefined") return "dashboard";
  const h = (window.location.hash || "").replace(/^#\/?/, "").trim();
  return VALID.has(h) ? h : "dashboard";
}

export default function AdminDashboard() {
  const [active, setActive] = useState(readHashTab());

  useEffect(() => {
    const onHash = () => setActive(readHashTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const select = (key) => {
    setActive(key);
    if (typeof window !== "undefined") {
      try { window.location.hash = `/${key}`; } catch { /* noop */ }
    }
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <AdminLayout active={active} onSelect={select}>
      {active === "dashboard" && <AdminAnalytics />}
      {active === "homepage" && (
        <div>
          <header className="mb-7">
            <p className="ed-overline mb-2">Content</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Homepage</h1>
            <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">
              Edit every block on the homepage — hero copy, featured residences, the about section, luxury highlights and footer. Changes go live the moment you save.
            </p>
          </header>
          <CMSEditor sections={["home"]} defaultActive="home" />
        </div>
      )}
      {active === "media" && <MediaLibrary />}
      {active === "seo" && (
        <div>
          <header className="mb-7">
            <p className="ed-overline mb-2">Content</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">SEO & Branding</h1>
            <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">
              Manage page titles, meta descriptions, Open Graph imagery, brand identity, navigation labels and footer copy.
            </p>
          </header>
          <CMSEditor sections={["seo", "brand", "navigation", "footer"]} defaultActive="seo" />
        </div>
      )}
      {active === "properties" && <PropertiesPanel />}
      {active === "bookings" && <BookingsPanel />}
      {active === "calendar" && <CalendarPanel />}
      {active === "guests" && <GuestsPanel />}
      {active === "payments" && <PaymentsPanel />}
      {active === "reviews" && (
        <ComingSoon
          title="Reviews"
          description="Verified guest reviews — moderated, ordered and styled in the editorial voice of the brand."
          bullets={[
            "Inbound review collection emails (post-stay)",
            "Moderation queue with star ratings + photos",
            "Pin standout reviews on the homepage and listings",
            "Anti-spam filtering",
          ]}
        />
      )}
      {active === "concierge" && <ConciergePanel />}
      {active === "longstay" && (
        <ComingSoon
          title="Long Stay"
          description="A dedicated lane for 30+ night residencies — different pricing, different paperwork, different guests."
          bullets={[
            "Long-stay-only inquiry form on the public site",
            "Custom pricing tiers per residence (28 / 60 / 90 nights)",
            "Light-touch tenancy paperwork generation",
            "Concierge handover workflow",
          ]}
        />
      )}
      {active === "owners" && <OwnersPanel />}
      {active === "brand-settings" && <BrandSettingsPanel />}
      {active === "platform-properties" && <PlatformPropertiesPanel />}
      {active === "knowledge-base" && <KnowledgeBasePanel />}
      {active === "knowledge-import" && <ImportKnowledgePanel />}
      {active === "concierge-shadow" && <ConciergeShadowPanel />}
      {active === "audit" && <AuditLogPanel />}
      {active === "flags" && <FeatureFlagsPanel />}
      {active === "settings" && <SettingsPanel />}
    </AdminLayout>
  );
}
